/**
 * Replaces `{key}` placeholders in a template string with values from an item object.
 * @param {string} template - Template string with `{key}` placeholders.
 * @param {Object} item - Data object used to fill placeholders.
 * @returns {string}
 */
export function fillTemplate(template, item) {
  return template.replace(/\{(\w+)\}/g, (_, key) => item?.[key] ?? '').trim();
}

/**
 * Formats a date as a relative label: Today, Yesterday, or "N days ago".
 * @param {string|Date} value
 * @returns {string}
 */
export function relativeDateLabel(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);

  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays} days ago`;
}

/**
 * Finds the most recent submission date on an item, checking common top-level
 * fields as well as `submissionHistory` entries.
 * @param {Object} item
 * @returns {string|null}
 */
function getLastSubmittedDate(item) {
  const history = Array.isArray(item?.submissionHistory) ? item.submissionHistory : [];
  const dates = [
    item?.lastSubmittedAt,
    item?.submittedAt,
    item?.updatedAt,
    ...history.map((entry) => entry?.submittedAt || entry?.updatedAt),
  ].filter(Boolean);

  if (!dates.length) return null;
  return dates.reduce((latest, curr) => (new Date(curr) > new Date(latest) ? curr : latest));
}

/**
 * Normalizes a raw API item into a `{ label, value, _raw }` option object.
 * Supports plain strings, pre-normalized objects, and raw API response shapes.
 * @param {*} item
 * @param {Object} [field] - Field config with optional `labelKey`, `valueKey`, `labelTemplate`.
 * @returns {{ label: string, value: string, _raw?: * }}
 */
export function normalizeOption(item, field) {
  if (typeof item === 'string') return { label: item, value: item };
  if (item?.label !== undefined && item?.value !== undefined && !field?.labelTemplate && !field?.valueKey) return item;

  const { labelKey, valueKey, labelTemplate } = field ?? {};

  const templateItem =
    labelTemplate?.includes('{lastdate}')
      ? { ...item, lastdate: relativeDateLabel(getLastSubmittedDate(item)) }
      : item;

  const label =
    (labelTemplate ? fillTemplate(labelTemplate, templateItem) : null) ||
    (labelKey ? item?.[labelKey] : null) ||
    item?.name ||
    item?.fullName ||
    item?.candidateName ||
    item?.title ||
    item?.displayName ||
    item?.text ||
    item?.applicantReferenceId ||
    item?.applicantRefrenceId ||
    String(Object.values(item || {})[0] ?? '');

  const value =
    (valueKey ? item?.[valueKey] : null) ??
    item?.id ??
    item?._id ??
    item?.applicantReferenceId ??
    item?.applicantRefrenceId ??
    String(Object.values(item || {})[0] ?? '');

  return { label: String(label), value: String(value), _raw: item };
}

/**
 * Resolves static options for a field from context or field defaults.
 * @param {Object} field
 * @param {Object} context
 * @returns {{ label: string, value: string }[]}
 */
export function resolveOptions(field, context) {
  const raw = context?.[field.optionsKey] ?? field.defaultOptions ?? [];
  return raw.map((opt) => normalizeOption(opt, field));
}

/**
 * Applies default values to a context object so consumers can rely on these keys always being present.
 * @param {Object} [context={}]
 * @returns {{ selectedIds: string[], selectedRecords: Object[], moduleName: string, removedIds: string[] }}
 */
export function getDefaultContext(context = {}) {
  return {
    selectedIds: [],
    selectedRecords: [],
    moduleName: '',
    removedIds: [],
    ...context,
  };
}
